import {tokens} from '@packages/utilities/styled';

import type {AvatarProps, AvatarSizes} from './_types';

export const avatarSizes = {
  'x-small': tokens.ui.avatar.xSmall,
  small: tokens.ui.avatar.small,
  medium: tokens.ui.avatar.medium,
  large: tokens.ui.avatar.large,
  'x-large': tokens.ui.avatar.xLarge,
};

export const getTextSize = (size: AvatarSizes) => {
  if (typeof size === 'number') {
    return Math.round(size * 0.4);
  }

  const baseSize = avatarSizes[size] || avatarSizes.medium;

  return `calc(${baseSize} * 0.4)`;
};

export const getIconSize = (size: AvatarSizes) => {
  switch (size) {
    case 'x-small':
      return 10;
    case 'small':
      return 12;
    case 'large':
      return 20;
    case 'x-large':
      return 24;
    case 'medium':
    default:
      return 16;
  }
};

export const avatarColors = ['purple', 'green', 'pink', 'blue'] as const;

/**
 * Enables the rendering of outlines and shadows without affecting
 * the specified Avatar size.
 *
 * Using (CSS) border would reduce the rendered sizing of the Avatar.
 */
export const getBoxShadowStyle = ({
  hasOutline,
  hasShadow,
  shape,
}: {
  hasOutline?: boolean;
  hasShadow?: boolean;
  shape?: AvatarProps['shape'];
}) => {
  if (!hasOutline && !hasShadow) {
    return undefined;
  }

  if (!hasOutline && hasShadow) {
    return `0 0 1px ${tokens.colors.uiAvatarShadow},0 2px 4px ${tokens.colors.uiAvatarShadow}`;
  }

  const shadows: string[] = [];

  /**
   * Square avatars are only allowed to have a border1 outline currently.
   */
  if (shape === 'square' && hasOutline) {
    shadows.push(`0 0 0 1px ${tokens.colors.border1}`);
  }

  if (hasShadow) {
    shadows.push(
      `0 0 1px 3px ${tokens.colors.uiAvatarShadow},0 2px 4px 3px ${tokens.colors.uiAvatarShadow}`
    );
  }

  return shadows.join(',');
};

export const colorFromString = (str: string = '') => {
  let n = 0;
  if (typeof str === 'string') {
    for (let i = 0; i < str.length; i++) {
      n += str.charCodeAt(i);
    }
  }
  return avatarColors[n % avatarColors.length] ?? avatarColors[0];
};

export const getAvatarColorVariant = (
  colorVariant: AvatarProps['colorVariant'],
  name: AvatarProps['name']
) => {
  switch (colorVariant) {
    case 'default':
    case undefined:
      return 'default';
    case 'pending':
      return 'pending';
    case 'randomColor':
      return colorFromString(name);
    default:
      return colorVariant;
  }
};
